import RENDERER_CONFIG from '@/configs/rendererConfig';
import { ProjectRenderer } from '../ProjectRenderer';
import { RenderUtils } from '../utils/RenderUtils';
import { Loader } from '../utils/Loader';
import { AssetsLib } from '../utils/AssetsLib';
import { Grid } from '../utils/Grid';
import { CreatorCustomBackground } from '@/types/creator';
import { getFileSrc } from '@/utils/utilities';
import { Attachment } from '@/types/types';
import {
	MeshStandardMaterial,
	Vector2Tuple,
	Mesh,
	PlaneGeometry,
	SRGBColorSpace,
	MathUtils,
} from 'three';

export interface SerializedProjectPlane {
	attachment: Attachment | null;
	dimensions: Vector2Tuple;
	rotation: number;
}

const DEFAULT_MATERIAL = new MeshStandardMaterial({
	color: 0xffffff,
});

export class ProjectPlane {
	private customMaterial: MeshStandardMaterial | null = null;
	private attachment: Attachment | null = null;
	private loader = Loader.getInstance();
	private assetsLib = AssetsLib.getInstance();
	grid: Grid | null = null;
	dimensions: Vector2Tuple = [
		RENDERER_CONFIG.GRID.SIZE,
		RENDERER_CONFIG.GRID.SIZE,
	];
	rotation = 0;
	plane: Mesh | null = null;

	constructor(private renderer: ProjectRenderer) {
		this.grid = new Grid(renderer, this);
	}

	createPlane() {
		this.dispose();

		const mat = this.customMaterial || DEFAULT_MATERIAL;

		this.plane = new Mesh(
			new PlaneGeometry(this.dimensions[0], this.dimensions[1]),
			mat.clone()
		);
		this.plane.rotateX(-(Math.PI / 2));
		this.plane.rotation.z = MathUtils.degToRad(this.rotation);
		this.plane.position.y = RENDERER_CONFIG.PLANE_Y;

		this.plane.receiveShadow = true;

		this.renderer.scene.add(this.plane);
	}

	initGrid() {
		this.grid!.createGrid();
	}

	reset() {
		this.dimensions = [RENDERER_CONFIG.GRID.SIZE, RENDERER_CONFIG.GRID.SIZE];
		this.rotation = 0;
		this.attachment = null;
		this.customMaterial = null;

		this.createPlane();
	}

	async handleCustomPlane(data: CreatorCustomBackground) {
		if (!data.attachment) throw new Error('MISSING_ATTACHMENT');

		this.dimensions = data.dimensions;
		this.rotation = data.rotation;
		this.attachment = data.attachment;

		await this.setCustomMaterial(getFileSrc(data.attachment.path));

		this.createPlane();
	}

	private async setCustomMaterial(src: string) {
		let material = this.assetsLib.materialCache.get(src);

		if (!material) {
			let texture = this.assetsLib.texturesCache.get(src);

			if (!texture) {
				texture = await this.loader.loadTexture(src);
				texture.colorSpace = SRGBColorSpace;

				this.assetsLib.texturesCache.set(src, texture);
			}

			material = new MeshStandardMaterial({
				map: texture,
			});
		}

		this.customMaterial = material;
	}

	private dispose() {
		if (!this.plane) return;
		RenderUtils.disposeObject(this.plane);
	}

	export(): SerializedProjectPlane {
		return {
			attachment: this.attachment,
			dimensions: this.dimensions,
			rotation: this.rotation,
		};
	}

	async loadFromData(data: SerializedProjectPlane) {
		this.attachment = data.attachment;
		this.dimensions = data.dimensions;
		this.rotation = data.rotation;

		if (data.attachment)
			await this.setCustomMaterial(getFileSrc(data.attachment.path));

		this.createPlane();
	}
}
